import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCameraList, useCameraStreamToken } from '@cguard/api-client/hooks/cameras';
import type { CameraId } from '@cguard/types';
import { LiveCameraPlayer, type LiveCameraState } from '@cguard/video-players';
import { Expand, History, Radio, RotateCcw, Shrink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CameraRail } from '~/components/camera-rail';
import { resolveCameraSelection } from '~/lib/camera-navigation';
import { useCameraStatusLabel, type CameraStatus } from '~/lib/camera-status';
import { withoutSeededDemoCameras } from '~/lib/production-roster';
import { useSiteNames } from '~/lib/site-names';

export function LivePage({ cameraId }: { readonly cameraId?: string } = {}): React.JSX.Element {
  const { t } = useTranslation('video');
  const statusLabel = useCameraStatusLabel();
  const navigate = useNavigate();
  const camerasQuery = useCameraList();
  const cameras = withoutSeededDemoCameras(camerasQuery.data ?? []);
  const selectedId = resolveCameraSelection(cameras, cameraId);
  const selected = cameras.find((camera) => camera.id === selectedId) ?? null;
  const siteNames = useSiteNames();
  const stream = useCameraStreamToken(selected?.status === 'live' ? selected.id : null);
  const viewerRef = React.useRef<HTMLDivElement | null>(null);
  const isFullscreen = useFullscreen(viewerRef);
  const [player, setPlayer] = React.useState<{
    readonly cameraId: CameraId | null;
    readonly state: LiveCameraState;
  }>({ cameraId: null, state: 'offline' });
  const isFixtureStream = stream.data?.url.includes('/v1/mock-evidence/') ?? false;
  const playerState: LiveCameraState = isFixtureStream
    ? 'playing'
    : player.cameraId === selectedId
      ? player.state
      : 'offline';
  const availability = liveAvailability(selected?.status ?? null, playerState);
  const siteName = selected ? siteNames.get(selected.siteId) : undefined;

  const selectCamera = (nextCameraId: CameraId): void => {
    void navigate({ to: '/live', search: { cameraId: nextCameraId }, replace: true });
  };

  const toggleFullscreen = (): void => {
    const request = isFullscreen
      ? document.exitFullscreen()
      : viewerRef.current?.requestFullscreen();
    void request?.catch(() => undefined);
  };

  return (
    <div className="video-workspace">
      <CameraRail cameras={cameras} selectedId={selectedId} onSelect={selectCamera} />
      <section className="viewer-column">
        <div className="viewer-titlebar">
          <div className="min-w-0">
            <p className="eyebrow">{t('live.eyebrow')}</p>
            <h1>{selected?.name ?? t('viewer.selectCamera')}</h1>
          </div>
          {selected ? (
            <div className={`viewer-state status-${availability}`} role="status" aria-live="polite">
              <span className="status-dot" aria-hidden />
              <span>
                {availability === 'unavailable' ? t('live.unavailable') : statusLabel(availability)}
              </span>
            </div>
          ) : null}
        </div>

        <div className="viewer-stage" ref={viewerRef}>
          {camerasQuery.isLoading ? (
            <ViewerMessage title={t('viewer.loadingCameras')} detail={t('live.loading.detail')} />
          ) : camerasQuery.isError ? (
            <ViewerMessage
              title={t('live.error.title')}
              detail={t('live.error.detail')}
              action={
                <button className="secondary-button" onClick={() => void camerasQuery.refetch()}>
                  <RotateCcw aria-hidden size={14} />
                  {t('common:actions.retry')}
                </button>
              }
            />
          ) : !selected ? (
            <ViewerMessage title={t('live.empty.title')} detail={t('live.empty.detail')} />
          ) : selected.status === 'offline' ? (
            <ViewerMessage title={t('live.offline.title')} detail={t('live.offline.detail')} />
          ) : selected.status === 'connecting' ? (
            <ViewerMessage
              title={t('live.connecting.title')}
              detail={t('live.connecting.detail')}
            />
          ) : isFixtureStream ? (
            <div
              className="fixture-live-frame fixture-static-player"
              role="img"
              aria-label={t('live.fixtureFrame', { name: selected.name })}
              data-testid="fixture-live-player"
            />
          ) : (
            <LiveCameraPlayer
              key={selected.id}
              cameraId={selected.id}
              cameraName={selected.name}
              className="h-full w-full rounded-none border-0"
              onStateChange={(state) => setPlayer({ cameraId: selected.id, state })}
            />
          )}

          {selected ? (
            <>
              {availability === 'live' ? (
                <div className="live-badge">
                  <Radio aria-hidden size={12} />
                  {statusLabel('live')}
                </div>
              ) : null}
              <button
                type="button"
                className="stage-button"
                aria-label={isFullscreen ? t('live.fullscreen.exit') : t('live.fullscreen.enter')}
                onClick={toggleFullscreen}
              >
                {isFullscreen ? <Shrink aria-hidden size={17} /> : <Expand aria-hidden size={17} />}
              </button>
            </>
          ) : null}
        </div>

        <div className="live-status-dock">
          <div className="min-w-0">
            {siteName ? <p>{siteName}</p> : null}
            <span>{selected ? t(`live.availability.${availability}`) : t('live.noCamera')}</span>
          </div>
          {selected?.playbackConfigured ? (
            <Link to="/playback" search={{ cameraId: selected.id }} className="secondary-button">
              <History aria-hidden size={14} />
              {t('live.openPlayback')}
            </Link>
          ) : null}
        </div>
      </section>
    </div>
  );
}

type LiveAvailability = CameraStatus | 'unavailable';

function liveAvailability(
  cameraStatus: CameraStatus | null,
  playerState: LiveCameraState,
): LiveAvailability {
  if (!cameraStatus || cameraStatus === 'offline') return 'offline';
  if (cameraStatus === 'connecting') return 'connecting';
  if (playerState === 'error' || playerState === 'still') return 'unavailable';
  if (playerState !== 'playing') return 'connecting';
  return 'live';
}

function useFullscreen(target: React.RefObject<HTMLElement | null>): boolean {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  React.useEffect(() => {
    const sync = (): void => {
      setIsFullscreen(target.current !== null && document.fullscreenElement === target.current);
    };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [target]);
  return isFullscreen;
}

function ViewerMessage({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="viewer-message">
      <span className="viewer-aperture" aria-hidden />
      <h3>{title}</h3>
      <p>{detail}</p>
      {action}
    </div>
  );
}
